import { __awaiter } from "tslib";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// tslint:disable-next-line:import-blacklist
import { useLocation } from 'react-router-dom';
import { useHistory, useStepRoutePath } from '@shared/routing';
import { useMediaQuery } from 'react-responsive';
import { useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAvailableServices, getExistGdsServicesOrInsurances, getHasAvailableSeats, getHasRequestedOrderServices, getIsBooked, getIsPriceFreezeProductSelected, getIsReadonly, getIsRedirectFromMetasearch, getIsTravellersDataFilled, getOrder, getPaymentIsRequired, getShowServicesBeforeBooking } from '../../store/order/selectors';
import { OrderGoal, reachGoal } from '../../../analytics';
import { downloadPDF, MOBILE_MIN_WIDTH } from '../../../utils';
import { useConfig } from '../../../context';
import { OrderDocumentType, useOrderDocumentQuery, useResendItineraryReceiptMutation, OrderStatus, useUpdateOrderMutation, useAdditionalServicesLazyQuery } from '@websky/graphql';
import { useConditionalPolling, useToggleable } from '../../../hooks';
import { ResendTicketsStages } from '../../../ResendTicketsForm/types';
import { getSteps, removeStepsWhenOrderHasPassengersData, removeStepsWhenRedirectFromMetasearch } from '../../steps';
import { StepsEnum } from '../../../types';
import { fillOrder } from '../../store/order/actions';
import { WEBSKY_SERVICE_OPEN_EVENT } from '../../constants';
import { getQueryPopupGoal } from '../../../DataLayerAnalytics/utils';
import { getQueryServicesParams, getUrlWithServicesParams } from '../../utils';
export class OrderIsArchivedTicketDownloadError extends Error {
    constructor() {
        super('Your order was archived. Please contact with client support to get a receipt');
        Object.setPrototypeOf(this, OrderIsArchivedTicketDownloadError.prototype);
    }
}
export const useDownloadOrderDocument = () => {
    const order = useSelector(getOrder);
    const [_error, setError] = useState(null);
    const { isOpen: loading, open: setLoading, close: setNotLoading } = useToggleable(false);
    const { isOpen: warningIsOpen, open: warningOpen, close: warningClose } = useToggleable(false);
    const { refetch: refetchDocuments, data, error } = useOrderDocumentQuery({
        skip: true
    });
    const handleDownload = useCallback((passengerId, documentType) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (loading) {
            return;
        }
        try {
            if (order.isArchived) {
                throw new OrderIsArchivedTicketDownloadError();
            }
            setLoading();
            reachGoal(OrderGoal.ItineraryReceiptDownload);
            const data = yield refetchDocuments({
                params: {
                    orderId: order.id,
                    documentType: documentType !== null && documentType !== void 0 ? documentType : OrderDocumentType.All,
                    travellerId: passengerId
                }
            });
            if ((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.OrderDocument) {
                downloadPDF(data.data.OrderDocument, `${getOrderDocumentFileName(documentType)}.pdf`);
            }
        }
        catch (er) {
            if (er instanceof Error) {
                setError(er);
            }
            warningOpen();
        }
        finally {
            setNotLoading();
        }
    }), [order.status]);
    return {
        handleDownload,
        document: data === null || data === void 0 ? void 0 : data.OrderDocument,
        loading,
        error: error || _error,
        warningIsOpen,
        warningClose
    };
};
const getOrderDocumentFileName = (type) => {
    switch (type) {
        case OrderDocumentType.AeroexpressTicket:
            return 'aeroexpress';
        case OrderDocumentType.EMD:
            return 'emd';
        case OrderDocumentType.NASTicket:
            return 'airport_services';
        case OrderDocumentType.EveryTravelTicket:
            return 'tour_voucher';
        default:
            return 'tickets';
    }
};
export const useResendTickets = (orderId) => {
    const UNAUTHORIZED_CODE = '403';
    const CLOSE_POPUP_AFTER_SUCCESSFUL_RESEND_AFTER = 1500;
    const RETURN_DEFAULT_STATUS_AFTER_SUCCESSFUL_RESEND_AFTER = 2000;
    const [resendStage, setResendStage] = useState(ResendTicketsStages.Default);
    const { isOpen, open, close } = useToggleable(false);
    const [resendEmail, { data, loading, error }] = useResendItineraryReceiptMutation();
    const checkIsUnauthorizedError = (error, resendStage) => {
        return resendStage === ResendTicketsStages.Error && (error === null || error === void 0 ? void 0 : error.message.includes(UNAUTHORIZED_CODE));
    };
    const handleResendEmail = useCallback((email) => __awaiter(void 0, void 0, void 0, function* () {
        setResendStage(ResendTicketsStages.Loading);
        return resendEmail({
            variables: {
                params: {
                    email,
                    orderId
                }
            }
        });
    }), [isOpen, resendStage, orderId, resendEmail]);
    const handleWarningModalClose = () => {
        setResendStage(ResendTicketsStages.Default);
    };
    const isUnauthorizedError = useMemo(() => checkIsUnauthorizedError(error, resendStage), [error, resendStage]);
    useEffect(() => {
        var _a;
        const resendResult = data === null || data === void 0 ? void 0 : data.ResendItineraryReceipt;
        if (error || ((_a = error === null || error === void 0 ? void 0 : error.graphQLErrors) === null || _a === void 0 ? void 0 : _a.length)) {
            setResendStage(ResendTicketsStages.Error);
            return;
        }
        if (!loading && !error && resendResult) {
            setResendStage(ResendTicketsStages.Success);
            setTimeout(close, CLOSE_POPUP_AFTER_SUCCESSFUL_RESEND_AFTER);
            setTimeout(() => {
                setResendStage(ResendTicketsStages.Default);
            }, RETURN_DEFAULT_STATUS_AFTER_SUCCESSFUL_RESEND_AFTER);
        }
    }, [data, loading, error]);
    return {
        openModal: open,
        closeModal: close,
        isModalOpen: isOpen,
        isUnauthorizedError,
        resendStage,
        handleWarningModalClose,
        handleResendEmail,
        loading,
        checkIsUnauthorizedError
    };
};
export const usePromoCodesData = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const { isOpen, setOpen, close } = useToggleable(false);
    return {
        data,
        setData,
        isOpen,
        setOpen,
        close,
        loading,
        setLoading
    };
};
const SYSTEM_PAGES = ['payment/successfull', 'payment/failed', 'refund', 'exchange', 'change-passenger-docs'];
export const useSteps = () => {
    const order = useSelector(getOrder);
    const availableServices = useSelector(getAvailableServices), { global: { stepsAvailable, servicesAvailable } } = useConfig();
    const isPaymentRequired = useSelector(getPaymentIsRequired), hasAvailableSeats = useSelector(getHasAvailableSeats), showServicesBeforeBooking = useSelector(getShowServicesBeforeBooking), isReadonly = useSelector(getIsReadonly), isTravellersDataFilled = useSelector(getIsTravellersDataFilled), isPriceFreezeProductSelected = useSelector(getIsPriceFreezeProductSelected), isBooked = useSelector(getIsBooked);
    const stepsRef = useRef();
    const location = useLocation(), match = useRouteMatch(), baseUrl = match.url;
    const history = useHistory();
    const stepRoutePath = useStepRoutePath(baseUrl);
    const isPassengerRoute = location.pathname === stepRoutePath('passengers');
    const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
    const isRedirectFromMetasearch = useSelector(getIsRedirectFromMetasearch);
    const steps = useMemo(() => {
        const steps = getSteps({
            order,
            availableServices,
            isPaymentRequired,
            hasAvailableSeats,
            showServicesBeforeBooking,
            configStepAvailable: stepsAvailable,
            configServicesAvailable: servicesAvailable
        });
        if (isMobile && isRedirectFromMetasearch) {
            return removeStepsWhenRedirectFromMetasearch(steps);
        }
        if (!isPriceFreezeProductSelected && isBooked && isTravellersDataFilled && !isPassengerRoute) {
            return removeStepsWhenOrderHasPassengersData(steps);
        }
        return steps;
    }, [
        order,
        availableServices,
        isPaymentRequired,
        isReadonly,
        hasAvailableSeats,
        showServicesBeforeBooking,
        stepsAvailable,
        isMobile,
        isRedirectFromMetasearch,
        isPriceFreezeProductSelected,
        isBooked,
        isTravellersDataFilled,
        isPassengerRoute
    ]);
    useEffect(() => {
        stepsRef.current = steps;
    }, [steps]);
    const getFirstAvailableStep = useCallback(() => {
        if (order.status !== OrderStatus.New) {
            return steps.find(step => step.type === StepsEnum.Order);
        }
        return steps.find(step => !step.hidden);
    }, [order.status, steps]);
    const currentStep = useMemo(() => {
        return steps.find(step => location.pathname === stepRoutePath(step.url));
    }, [steps, location.pathname, stepRoutePath]);
    const isSystemPage = SYSTEM_PAGES.some(path => location.pathname.includes(path));
    if ((!currentStep || currentStep.hidden) && !isSystemPage) {
        const currentStepIndex = steps.findIndex(step => location.pathname === stepRoutePath(step.url)), nextAvailableStep = currentStepIndex === -1
            ? getFirstAvailableStep()
            : steps.find((step, index) => !step.hidden && index > currentStepIndex);
        setTimeout(() => {
            var _a, _b;
            const step = (_a = nextAvailableStep === null || nextAvailableStep === void 0 ? void 0 : nextAvailableStep.url) !== null && _a !== void 0 ? _a : (_b = getFirstAvailableStep()) === null || _b === void 0 ? void 0 : _b.url;
            let bookingURL = `/booking/${order.id}/${step}`;
            if (step === 'order') {
                bookingURL = getUrlWithServicesParams(bookingURL, location);
            }
            history.replace(bookingURL);
        });
    }
    const nextStep = useCallback(() => {
        const currentStepIndex = stepsRef.current.findIndex(step => location.pathname === stepRoutePath(step.url));
        const nextStep = stepsRef.current.find((step, index) => !step.hidden && index > currentStepIndex);
        if (nextStep) {
            history.push(`/booking/${order.id}/${nextStep.url}`);
        }
    }, [order.id, location.pathname, stepRoutePath, history]);
    const prevStep = useCallback(() => {
        const currentStepIndex = steps.findIndex(step => location.pathname === stepRoutePath(step.url));
        for (let i = currentStepIndex - 1; i >= 0; i--) {
            if (!steps[i].hidden) {
                history.push(`/booking/${order.id}/${steps[i].url}`);
                return;
            }
        }
    }, [order.id, steps, location.pathname, stepRoutePath, history]);
    const setStep = useCallback((stepType) => {
        const nextStepIndex = steps.findIndex(step => step.type === stepType);
        history.push(`/booking/${order.id}/${steps[nextStepIndex].url}`);
    }, [order.id, steps, history]);
    return {
        steps: steps.filter(step => !step.hidden),
        currentStep,
        nextStep,
        prevStep,
        setStep
    };
};
export const useUpdateTravellerServices = () => {
    const dispatch = useDispatch();
    const order = useSelector(getOrder);
    const hasRequestedOrderServices = useSelector(getHasRequestedOrderServices);
    const [updateOrder] = useUpdateOrderMutation();
    const onUpdateOrder = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const { data } = yield updateOrder({ variables: { id: order.id } });
        if (data === null || data === void 0 ? void 0 : data.UpdateOrder) {
            dispatch(fillOrder(data.UpdateOrder));
        }
    }), [order === null || order === void 0 ? void 0 : order.id, updateOrder]);
    useConditionalPolling(onUpdateOrder, hasRequestedOrderServices, {
        interval: 5000,
        progressiveIntervalPerQuery: true
    });
};
export const useQueryControlledServicesModal = () => {
    const history = useHistory();
    const location = useLocation();
    const existGdsServicesOrInsurances = useSelector(getExistGdsServicesOrInsurances);
    useEffect(() => {
        if (!existGdsServicesOrInsurances) {
            return;
        }
        const { serviceRfisc, openServiceType, serviceSubType, insuranceCode } = getQueryServicesParams(location);
        if (openServiceType) {
            const event = new CustomEvent(WEBSKY_SERVICE_OPEN_EVENT, {
                detail: {
                    service: {
                        rfisc: serviceRfisc,
                        type: openServiceType,
                        subType: serviceSubType,
                        insuranceCode
                    },
                    meta: {
                        custom_ecommerce_name: getQueryPopupGoal(openServiceType, serviceSubType, insuranceCode, serviceRfisc)
                    },
                    callback: () => __awaiter(void 0, void 0, void 0, function* () { })
                }
            });
            history.replace({ search: '' });
            setTimeout(() => document.dispatchEvent(event));
        }
    }, [existGdsServicesOrInsurances, location]);
};
export const useQueryEventToggleable = ({ serviceTypes, serviceRfisc, serviceSubTypes, insuranceCodes, onOpen }) => {
    const onEventHandler = useCallback((event) => {
        const { type, rfisc, subType, insuranceCode } = event.detail.service;
        if (!serviceTypes.includes(type)) {
            return;
        }
        if ((rfisc || subType || insuranceCode) &&
            (serviceRfisc || serviceSubTypes || insuranceCodes || []).length) {
            const isIncludeRfisc = serviceRfisc.includes(rfisc);
            const isIncludeSubType = serviceSubTypes.includes(subType);
            const isIncludeInsuranceCode = insuranceCodes.includes(insuranceCode);
            if (!isIncludeRfisc && !isIncludeSubType && !isIncludeInsuranceCode) {
                return;
            }
        }
        onOpen();
    }, [serviceTypes, serviceRfisc, serviceSubTypes, insuranceCodes, onOpen]);
    useEffect(() => {
        document.addEventListener(WEBSKY_SERVICE_OPEN_EVENT, onEventHandler);
        return () => document.removeEventListener(WEBSKY_SERVICE_OPEN_EVENT, onEventHandler);
    }, []);
};
export const useGdsServiceEventToggleable = ({ services, serviceTypes, serviceSubTypes = [], onOpen }) => {
    const { availableServiceRfisc, availableServiceSubTypes } = useMemo(() => {
        const serviceRfiscSet = new Set();
        const serviceSubTypeSet = new Set(serviceSubTypes);
        services.forEach(service => {
            serviceRfiscSet.add(service.rfisc);
            if (service === null || service === void 0 ? void 0 : service.baggageType) {
                // BaggageService
                const baggageService = service;
                serviceSubTypeSet.add(baggageService.baggageType);
            }
            else if (service === null || service === void 0 ? void 0 : service.subType) {
                // Vip service
                const vipService = service;
                serviceSubTypeSet.add(vipService.subType);
            }
        });
        return {
            availableServiceRfisc: [...serviceRfiscSet],
            availableServiceSubTypes: [...serviceSubTypeSet]
        };
    }, [services, serviceSubTypes]);
    useQueryEventToggleable({
        serviceTypes,
        serviceRfisc: availableServiceRfisc,
        serviceSubTypes: availableServiceSubTypes,
        onOpen
    });
};
export const useNasServiceEventToggleable = ({ services, serviceTypes, onOpen }) => {
    const availableServiceSubTypes = useMemo(() => {
        const serviceSubTypeSet = new Set();
        services.forEach(service => serviceSubTypeSet.add(service.type));
        return [...serviceSubTypeSet];
    }, [services]);
    useQueryEventToggleable({
        serviceTypes,
        serviceSubTypes: availableServiceSubTypes,
        onOpen
    });
};
export const useQueryParamsOrderUpdate = () => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const order = useSelector(getOrder);
    const orderRef = useRef(order);
    orderRef.current = order;
    const [updateOrder] = useUpdateOrderMutation();
    const [loadAdditionalServices] = useAdditionalServicesLazyQuery({
        fetchPolicy: 'no-cache',
        onCompleted: data => {
            if ((data === null || data === void 0 ? void 0 : data.AdditionalServices) && orderRef.current) {
                dispatch(fillOrder(Object.assign(Object.assign({}, orderRef.current), { additionalServices: data.AdditionalServices })));
            }
        }
    });
    useEffect(() => {
        const { updateOrder: shouldUpdateOrder, updateServices: shouldUpdateServices } = getQueryServicesParams(location);
        if (!(order === null || order === void 0 ? void 0 : order.id) || (!shouldUpdateOrder && !shouldUpdateServices)) {
            return;
        }
        const runMutations = () => __awaiter(void 0, void 0, void 0, function* () {
            if (shouldUpdateOrder) {
                const { data } = yield updateOrder({ variables: { id: order.id } });
                if (data === null || data === void 0 ? void 0 : data.UpdateOrder) {
                    dispatch(fillOrder(data.UpdateOrder));
                }
            }
            if (shouldUpdateServices) {
                loadAdditionalServices({ variables: { id: order.id } });
            }
            history.replace({ search: '' });
        });
        runMutations();
    }, [order === null || order === void 0 ? void 0 : order.id, location.search, updateOrder, loadAdditionalServices, dispatch, history]);
};
